import React, { useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { LscAuthGate } from '../hooks/useLscAuthGate';
import { useLeague } from '../hooks/useLeague';
import { useLeagueSetupProgress } from '../hooks/useLeagueSetupProgress';
import CommissionerOnlyGate from '../components/CommissionerOnlyGate';
import LeagueOnboardingShell, {
  resolveInitialOnboardingStep,
} from '../components/LeagueOnboardingShell';
import LoadingState from '../components/LoadingState';
import ErrorBanner from '../components/ErrorBanner';
import { resolveLeagueSetupWizardFromReadiness } from '../leagueReadinessMapper';
import { getDashboardRenderState } from '../leagueDashboardState';
import { lscReturnTo } from '../routing';
import type { LscLeagueSetupProgressStepId, LscLeague } from '../types';
import { isOnboardingStepId } from '../onboardingSteps';
import { useLscContentColumn } from '../lscContentColumn';

const SETUP_STEP_SAVE_ERROR_COPY =
  'Could not save your setup progress. Check your connection and try again.';

const LscLeagueSetupWizardPage: React.FC = () => {
  useLscContentColumn('narrow');
  const { leagueId } = useParams<{ leagueId: string }>();
  return (
    <LscAuthGate returnTo={lscReturnTo(`/leagues/${leagueId}/setup`)}>
      <CommissionerOnlyGate>
        <LeagueSetupWizardContent />
      </CommissionerOnlyGate>
    </LscAuthGate>
  );
};

function LeagueSetupWizardContent() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { league, isOwner, isCommissioner, loading, error, refreshLeague } = useLeague();
  // setup-progress embeds readiness — one round trip (was double rebuild before).
  const progressState = useLeagueSetupProgress(leagueId);

  const readiness = progressState.progress?.readiness ?? null;

  const wizardResult = useMemo(
    () => (readiness ? resolveLeagueSetupWizardFromReadiness(readiness, { isOwner }) : null),
    [readiness, isOwner]
  );

  const activeStepId = useMemo(
    () => resolveInitialOnboardingStep(searchParams.get('step'), progressState.progress),
    [searchParams, progressState.progress]
  );

  const handleSetupRefresh = useCallback(() => {
    void Promise.all([progressState.reload(), refreshLeague()]);
  }, [progressState, refreshLeague]);

  const handleLeagueUpdated = useCallback(
    (_updated: LscLeague) => {
      void refreshLeague();
    },
    [refreshLeague]
  );

  const leagueArchived = league?.status === 'archived';

  const handleStepChange = useCallback(
    (stepId: LscLeagueSetupProgressStepId) => {
      if (!isOnboardingStepId(stepId)) {
        return;
      }
      void (async () => {
        const saved = await progressState.saveCurrentStep(stepId);
        if (!saved) {
          return;
        }
        const next = new URLSearchParams(searchParams);
        next.set('step', stepId);
        setSearchParams(next, { replace: true });
      })();
    },
    [progressState, searchParams, setSearchParams]
  );

  const handleRetryStepSave = useCallback(() => {
    if (!progressState.lastFailedStepId) {
      return;
    }
    handleStepChange(progressState.lastFailedStepId);
  }, [handleStepChange, progressState.lastFailedStepId]);

  const renderState = getDashboardRenderState({
    loading,
    error,
    hasLeague: Boolean(league),
  });

  if (renderState === 'loading' || progressState.loading) {
    return (
      <div data-testid="league-setup-loading">
        <LoadingState message="Loading league setup…" />
      </div>
    );
  }
  if (renderState === 'error') {
    return <ErrorBanner message={error!} />;
  }
  if (renderState === 'missing') {
    return <ErrorBanner message="League workspace could not be loaded." />;
  }

  if (progressState.authError === 'unauthorized') {
    return <ErrorBanner message="Sign in to view league setup status." />;
  }

  if (progressState.authError === 'forbidden') {
    return <ErrorBanner message="You do not have permission to view league setup status." />;
  }

  if (progressState.error || !wizardResult || !readiness || !leagueId || !league) {
    return (
      <div className="space-y-3 max-w-3xl" data-testid="league-setup-wizard-page">
        <ErrorBanner
          message={
            progressState.error ??
            'Unable to load setup status. Try again in a moment.'
          }
        />
        <button
          type="button"
          className="btn-secondary text-sm"
          data-testid="league-setup-retry"
          onClick={() => {
            void progressState.reload();
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="league-setup-wizard-page">
      <div>
        <h1 className="text-2xl font-bold text-white mb-1">League setup</h1>
        <p className="text-sm text-secondary-400">
          Guided onboarding for {league!.name} — subscription, schedule, members, Twitch, and
          automation readiness.
        </p>
      </div>

      {progressState.saveError ? (
        <div className="space-y-2" data-testid="league-setup-step-save-error">
          <ErrorBanner message={SETUP_STEP_SAVE_ERROR_COPY} />
          <button
            type="button"
            className="btn-secondary text-sm"
            onClick={handleRetryStepSave}
            disabled={!progressState.lastFailedStepId}
          >
            Retry save
          </button>
        </div>
      ) : null}

      <LeagueOnboardingShell
        leagueId={leagueId}
        leagueName={league.name}
        league={league}
        readiness={readiness}
        isOwner={isOwner}
        isCommissioner={isCommissioner}
        wizardResult={wizardResult}
        setupProgress={progressState.progress}
        activeStepId={activeStepId}
        onStepChange={handleStepChange}
        onLeagueUpdated={handleLeagueUpdated}
        onSetupRefresh={handleSetupRefresh}
        progressLoading={progressState.loading}
        leagueArchived={leagueArchived}
      />
    </div>
  );
}

export default LscLeagueSetupWizardPage;
